import { VoiceRecorder } from 'capacitor-voice-rec';
import uuid from 'uuid-v4';
import { Timestamp } from '@/firebaseConfig.js';
import { useLocalNotification } from '@/hooks/useLocalNotification';
import { SendError, SendSuccess } from '@/helpers';
import store from '@/store/store';
import { Sentry } from '@/vendors/Sentry';

export class VoiceRecorderWrapper {
  static name = null;

  constructor(caller, { notifications = false } = {}) {
    VoiceRecorderWrapper.name = null;
    this.caller = caller;
    this.setState = (status) =>
      store.commit('setRecordStatus', {
        status,
        caller,
      });

    if (notifications) {
      this.localNotifications = useLocalNotification(() => this.stopRecording(), {
        title: 'Recording in Progress',
        body: 'Remember to stop recording after call is over',
        actionTypeId: 'recording-in-progress',
        ongoing: true,
      });
    }
  }

  async startRecording({ name = 'Untitled', organizationId = null } = {}) {
    VoiceRecorderWrapper.name = name;
    VoiceRecorderWrapper.organizationId = organizationId;
    try {
      await this.localNotifications?.sendNotification();
      await VoiceRecorder.startRecording({
        smallIcon: 'ic_notification',
        useForegroundService: true,
      });
      this.setState('RECORDING');
      SendSuccess('Recording started');
    } catch (e) {
      Sentry.captureException(e);
      SendError('Recording start failed');
      await this.syncStatus();
    }
  }
  async resumeRecording() {
    try {
      await VoiceRecorder.resumeRecording();
      this.setState('RECORDING');
    } catch (e) {
      Sentry.captureException(e);
      SendError(e);
      await this.syncStatus();
    }
  }
  async pauseRecording() {
    try {
      await VoiceRecorder.pauseRecording();
      this.setState('PAUSED');
    } catch (e) {
      Sentry.captureException(e);
      SendError(e);
      await this.syncStatus();
    }
  }

  async stopRecording() {
    try {
      const result = await VoiceRecorder.stopRecording();
      this.setState('NONE');
      this.localNotifications?.cancelNotification();
      if (!!result.value?.recordDataBase64) {
        const id = uuid();
        const now = Timestamp.now();
        const recording = {
          status: 'local',
          id,
          createdAt: {
            seconds: now.seconds,
            nanoseconds: now.nanoseconds,
          },
          name: VoiceRecorderWrapper.name,
          organizationId: VoiceRecorderWrapper.organizationId,
          audioURL: `data:${result.value.mimeType};base64,${result.value.recordDataBase64}`,
          mimeType: result.value.mimeType,
          duration: result.value.msDuration,
        };
        delete result.value.recordDataBase64;
        return recording;
      }
    } catch (e) {
      SendError(e);
      Sentry.captureException(e);
      await this.syncStatus();
    }
    VoiceRecorderWrapper.name = null;
    VoiceRecorderWrapper.organizationId = null;
  }

  resetStatus() {
    store.commit('setRecordStatus', {
      status: 'NONE',
      caller: null,
    });
  }

  async syncStatus() {
    const { status, caller } = await VoiceRecorderWrapper.getCurrentStatus();
    if (status === 'NONE') {
      this.resetStatus();
    } else {
      this.setState({
        status,
        caller,
      });
    }
  }

  static async getCurrentStatus() {
    const { status } = await VoiceRecorder.getCurrentStatus();
    const storeStatus = store.state.recordStatus;
    return {
      status: status,
      caller: storeStatus.caller,
    };
  }

  static async requestRecording() {
    const canRecord = await VoiceRecorder.canDeviceVoiceRecord();
    if (!canRecord.value) {
      SendError('Device does not support audio recording. Please check your app permissions');
      return false;
    } else {
      const result = await VoiceRecorder.requestAudioRecordingPermission();
      if (result.value) {
        return true;
      } else {
        SendError('Cannot access microphone. Please check your app settings');
        return false;
      }
    }
  }
}
